/**
 * AskChef — the grocery wiring for the shared AI assistant.
 *
 * Owns three things and no product logic: the always-visible chef-hat
 * launcher, the small friendly card under the list that shows BOTH personas,
 * and the chat window. If `ask_chef_enabled` is false for the organization the
 * whole thing RENDERS NOTHING — the button is removed, never greyed out.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import AssistantLauncher from '../assistant/AssistantLauncher';
import AssistantChat from '../assistant/AssistantChat';
import useChefConversation from '../assistant/useChefConversation';

const API = (typeof window !== 'undefined' ? window.location.origin : '');
export const CHEF_HAT = '/static/icons/grocery/chef-hat.svg';

export function useAskChef(session, lang) {
    const [config, setConfig] = useState(null);
    const api = useCallback(async (path, { method = 'GET', body } = {}) => {
        const r = await fetch(`${API}/api/ask-chef${path}`, {
            method,
            headers: { 'Content-Type': 'application/json', 'X-Grocery-Session': session },
            body: body ? JSON.stringify(body) : undefined,
        });
        if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `http_${r.status}`);
        return r.json();
    }, [session]);

    const load = useCallback(() => {
        api(`/config?lang=${lang}`).then(setConfig).catch(() => setConfig({ enabled: false }));
    }, [api, lang]);
    useEffect(() => { load(); }, [load]);
    return { config, api, reload: load };
}

/** The card under the list: both personas, one tap each. */
export function ChefPersonaCard({ config, onOpen }) {
    const { t } = useTranslation();
    if (!config?.enabled) return null;
    return (
        <div className="space-y-2 rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700/60 dark:bg-slate-900/40"
            data-testid="chef-persona-card">
            <p className="flex items-center gap-2 text-sm font-bold text-foreground">
                <img src="/static/icons/grocery/chef-hat-avatar.svg" alt="" aria-hidden="true"
                    className="h-6 w-6" />
                {t('askChef.cardTitle')}
            </p>
            <p className="text-xs text-muted-foreground">{t('askChef.cardLine')}</p>
            <div className="flex flex-wrap gap-2">
                {(config.personas || []).map((p) => (
                    <button key={p.key} onClick={() => onOpen(p.key)}
                        data-testid={`chef-card-persona-${p.key}`}
                        className="flex min-h-[56px] items-center gap-2 rounded-full border border-slate-200 pl-1.5 pr-4 text-left transition-colors hover:border-[#13265C] dark:border-slate-700">
                        <img src={p.avatar} alt="" aria-hidden="true" className="h-10 w-10" />
                        <span>
                            <span className="block text-sm font-bold text-foreground">{p.name}</span>
                            <span className="block text-[11px] text-muted-foreground">
                                {t(p.speciality === 'baking' ? 'askChef.bakingHelper' : 'askChef.cookingHelper')}
                            </span>
                        </span>
                    </button>
                ))}
            </div>
        </div>
    );
}

/** THE FIRST-VISIT WELCOME, IN THE PAGE AND NOT OVER IT.
 *
 *  It used to be a floating bubble pinned above the chef button, which meant
 *  that on a long list (a seeded household has 7-8 rows plus favourites) it sat
 *  on a row and covered the words. A floating element CANNOT be made safe for
 *  every list length, because the content scrolls underneath it. So the welcome
 *  line now lives IN THE FLOW of the list, directly under the household's name:
 *  it takes its own space, nothing is ever behind it, and it is impossible for
 *  it to cover text at any length, width, language or mode. It still closes
 *  itself after 8 seconds and is still remembered server-side. */
export function ChefWelcomeNote({ config, api, quiet = false }) {
    const { t } = useTranslation();
    const [show, setShow] = useState(false);
    const told = useRef(false);
    useEffect(() => {
        setShow(!!config?.enabled && !config?.bubble_seen && !quiet
                && (config?.welcome_style || 'inline') === 'inline');
    }, [config, quiet]);
    const dismiss = useCallback(() => {
        setShow(false);
        if (told.current) return;
        told.current = true;
        api('/preferences', { method: 'POST', body: { bubble_seen: true } }).catch(() => {});
    }, [api]);
    useEffect(() => {
        if (!show) return undefined;
        const id = setTimeout(dismiss, 8000);
        return () => clearTimeout(id);
    }, [show, dismiss]);
    if (!show) return null;
    return (
        <div data-testid="chef-welcome-note"
            className="flex items-start gap-3 rounded-2xl border border-[#F58220]/40 bg-[#FFF4EA] px-3 py-2.5 dark:border-[#F58220]/40 dark:bg-[#2A1B0C]">
            <img src="/static/icons/grocery/chef-hat-avatar.svg" alt="" aria-hidden="true"
                className="mt-0.5 h-6 w-6 shrink-0" />
            <p className="flex-1 text-sm font-medium text-[#13265C] dark:text-[#FFC08A]">
                {t('askChef.firstBubble')}
            </p>
            <button onClick={dismiss} aria-label={t('askChef.close')}
                data-testid="chef-welcome-note-close"
                className="-mr-1 -mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[#13265C]/60 hover:bg-black/5 dark:text-[#FFC08A]/70 dark:hover:bg-white/10">
                <X className="h-4 w-4" />
            </button>
        </div>
    );
}

/** The launcher + the window. Mounted once per grocery shopper surface.
 *
 *  THE CONVERSATION LIVES HERE, not in the window, so closing the window
 *  mid-answer cannot lose the answer and reopening in the same visit comes
 *  back to the same chef and the same conversation. */
export default function AskChef({ session, lang, config, api, reload, onAdded,
    openWith, onOpened, quietBubble = false, hideLauncher = false, freezePosition = false }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const convo = useChefConversation({ api, config, lang });

    useEffect(() => {
        if (!openWith) return;
        // Tapping a chef on the card: that chef's own conversation. Tapping the
        // one already open just brings it back.
        if (convo.persona !== openWith) convo.startWith(openWith);
        setOpen(true);
        onOpened?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openWith, onOpened]);

    if (!config?.enabled) return null;

    const seenBubble = () => {
        api('/preferences', { method: 'POST', body: { bubble_seen: true } }).catch(() => {});
    };

    const floating = (config?.welcome_style || 'inline') === 'floating';
    return (
        <>
            {/* THE WELCOME LINE IS IN THE PAGE BY DEFAULT (ChefWelcomeNote), because
                a floating bubble sat on a list row. Set the organization's
                `welcome_style` to "floating" and the old bubble comes straight back. */}
            <AssistantLauncher icon={CHEF_HAT} label={t('askChef.launcher')}
                hint={t('askChef.firstBubble')}
                showBubble={floating && !config.bubble_seen && !quietBubble}
                onBubbleSeen={seenBubble} testId="chef-hat-btn" hidden={hideLauncher}
                freeze={freezePosition}
                onOpen={() => setOpen(true)} />
            {open ? (
                <AssistantChat api={api} config={config} lang={lang} convo={convo}
                    onAdded={(p, opts) => { onAdded?.(p, opts); reload?.(); }}
                    onClose={() => { setOpen(false); reload?.(); }} />
            ) : null}
        </>
    );
}
